if (!customElements.get('sb-frequently-bought-together')) {
  class SBFrequentlyBoughtTogether extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      if (this.querySelector('[data-fbt-content]')) {
        this.bind();
        return;
      }

      const url = this.dataset.url;
      if (!url) {
        this.remove();
        return;
      }

      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error('Recommendations could not be loaded.');
          return response.text();
        })
        .then((text) => {
          const container = document.createElement('div');
          container.innerHTML = text;
          const source = container.querySelector('sb-frequently-bought-together');
          const content = source && source.querySelector('[data-fbt-content]');

          if (!content) {
            this.remove();
            return;
          }

          this.innerHTML = source.innerHTML;
          this.bind();
        })
        .catch(() => {
          this.remove();
        });
    }

    bind() {
      this.items = Array.from(this.querySelectorAll('[data-fbt-item]'));
      this.addButton = this.querySelector('[data-fbt-add]');
      this.total = this.querySelector('[data-fbt-total]');
      this.status = this.querySelector('[data-fbt-status]');

      if (!this.items.length || !this.addButton || !this.total) {
        this.remove();
        return;
      }

      this.items.forEach((item) => {
        const toggle = item.querySelector('[data-fbt-toggle]');
        const variant = item.querySelector('[data-fbt-variant]');

        toggle?.addEventListener('change', () => this.update());
        variant?.addEventListener('change', () => {
          const option = variant.selectedOptions && variant.selectedOptions[0];
          const itemPrice = item.querySelector('[data-fbt-item-price]');
          if (option && itemPrice) itemPrice.textContent = this.formatMoney(Number(option.dataset.price || 0));
          this.update();
        });
      });

      this.addButton.addEventListener('click', () => this.addSelected());
      this.update();
    }

    selectedItems() {
      return this.items
        .filter((item) => {
          const toggle = item.querySelector('[data-fbt-toggle]');
          return toggle && toggle.checked && !toggle.disabled;
        })
        .map((item) => {
          const variant = item.querySelector('[data-fbt-variant]');
          const option = variant?.selectedOptions?.[0];
          return {
            id: Number(variant?.value || 0),
            quantity: 1,
            price: Number(option?.dataset.price || variant?.dataset.price || 0),
          };
        })
        .filter((item) => item.id > 0);
    }

    update() {
      const selected = this.selectedItems();
      const total = selected.reduce((sum, item) => sum + item.price, 0);
      this.total.textContent = this.formatMoney(total);
      this.addButton.disabled = selected.length === 0;
      this.items.forEach((item) => {
        const toggle = item.querySelector('[data-fbt-toggle]');
        item.classList.toggle('sb-fbt__item--excluded', Boolean(toggle && !toggle.checked));
      });
    }

    formatMoney(cents) {
      const format = this.dataset.moneyFormat || '${{amount}}';
      const token = format.match(/\{\{\s*(\w+)\s*\}\}/);
      const placeholder = token ? token[1] : 'amount';

      const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
        if (!Number.isFinite(number)) return '0';
        const parts = (number / 100).toFixed(precision).split('.');
        parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
        return parts[1] ? parts.join(decimal) : parts[0];
      };

      const values = {
        amount: formatWithDelimiters(cents, 2, ',', '.'),
        amount_no_decimals: formatWithDelimiters(cents, 0, ',', '.'),
        amount_with_comma_separator: formatWithDelimiters(cents, 2, '.', ','),
        amount_no_decimals_with_comma_separator: formatWithDelimiters(cents, 0, '.', ','),
        amount_with_apostrophe_separator: formatWithDelimiters(cents, 2, "'", '.'),
      };

      return token ? format.replace(token[0], values[placeholder] || values.amount) : `${format}${values.amount}`;
    }

    async addSelected() {
      const selected = this.selectedItems();
      if (!selected.length) return;

      this.setBusy(true);
      this.setStatus('');

      const cartSurface = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const sections = cartSurface?.getSectionsToRender
        ? cartSurface.getSectionsToRender().map((section) => section.section || section.id)
        : ['cart-drawer', 'cart-icon-bubble'];

      cartSurface?.setActiveElement?.(document.activeElement);

      try {
        const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: selected.map(({ id, quantity }) => ({ id, quantity })),
            sections,
            sections_url: window.location.pathname,
          }),
        });

        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'These items could not be added.');
        }

        if (cartSurface?.renderContents && result.sections) {
          cartSurface.renderContents(result);
          cartSurface.classList.remove('is-empty');

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            fetch((window.Shopify?.routes?.root || '/') + 'cart.js')
              .then((cartResponse) => cartResponse.json())
              .then((cartData) => publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'sb-frequently-bought-together',
                cartData,
              }))
              .catch(() => {});
          }
        } else {
          window.location.assign((window.Shopify?.routes?.root || '/') + 'cart');
          return;
        }

        if (cartSurface?.open) cartSurface.open();
        this.setStatus('Selected items added to your cart.');
      } catch (error) {
        this.setStatus(error.message || 'These items could not be added. Please try again.', true);
      } finally {
        this.setBusy(false);
      }
    }

    setBusy(isBusy) {
      this.addButton.disabled = isBusy || this.selectedItems().length === 0;
      this.addButton.setAttribute('aria-busy', String(isBusy));
    }

    setStatus(message, isError = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.dataset.error = String(isError);
    }
  }

  customElements.define('sb-frequently-bought-together', SBFrequentlyBoughtTogether);
}
